import _ from "lodash";
import React, { useState, useRef } from "react";
import classes from "./FileViewer.scss";
import IframeWrapper from "./IframeWrapper";
import {
  GOOGLE_FILE_VIEWER_URL,
  MS_FILE_VIEWER_URL,
  ALLOWED_PLAYABLE_VIDEO_FILE_TYPE,
  GOOGLE_FILE_VIEWER_SUPPORTED_MIMETYPE,
} from "Constants";
import { TABLET_LANDSCAPE_MEDIA_QUERY } from "Constants/stringConstants";
import I18nHOC from "UIComponents/I18nHOC";
import EmbedVideoPlayer from "UIComponents/EmbedVideoPlayer";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import { ArrowTriangleOutlined } from "@toddle-edu/ds-icons";
import GoogleDocsPlaybackWrapper from "UIComponents/GoogleDocsPlaybackWrapper";
import Loading from "UIComponents/Loading";
import { GiveFeedbackIcon } from "SvgComponents";
import ReactPlayer from "react-player";
import { delay } from "Utils";
import ACLStore from "lib/aclStore";
import useMediaQuery from "Hooks/UseMediaQuery";

const googleSlidesLinkRegex = /https:\/\/docs.google.com\/(presentation)/;
const googleFormsLinkRegex = /https:\/\/docs\.google\.com\/forms\//;

const FileViewer = React.memo(
  ({
    file,
    mode,
    showFileHeader,
    shouldShowGiveFeedbackButton,
    onClickGiveFeedback,
    t,
    useIframePartialLoader,
    showGoogleDocsPlaybackButton,
    playbackButtonPositionStyle,
  }) => {
    const mediaType = _.get(file, "type", "");
    const mimeType = _.get(file, "mimeType", "");
    let fileUrl = _.get(file, "url", "");
    const signedUrl = _.get(file, "signedUrl", "");
    const id = _.get(file, "id", "");
    const name = _.get(file, "name", "");

    const isVideoLink =
      (mediaType == "LINK" && ReactPlayer.canPlay(fileUrl)) ||
      _.includes(ALLOWED_PLAYABLE_VIDEO_FILE_TYPE, mimeType);

    let openInViewer = false;
    let useGoogleDocsEmbedViewer = false;

    if (mediaType == "FILE" && mimeType != "application/pdf") {
      openInViewer = true;
    }
    if (openInViewer) {
      useGoogleDocsEmbedViewer =
        ACLStore.can("FeatureFlag:EnableGoogleEmbededViewer") ||
        (ACLStore.can("FeatureFlag:UseGoogleFileViewerForGdocs") &&
          _.includes(GOOGLE_FILE_VIEWER_SUPPORTED_MIMETYPE, mimeType));
    }
    const isMobile = useMediaQuery(TABLET_LANDSCAPE_MEDIA_QUERY);

    const isGoogleForm =
      mediaType == "LINK" && googleFormsLinkRegex.test(fileUrl);

    if (isGoogleForm) {
      fileUrl = fileUrl.replace(/\/(edit|preview)(\?|#|$)/, "/viewform$2");
    } else if (
      (mode === "preview" && mediaType == "LINK") ||
      (ACLStore.can("FeatureFlag:EmbedSlidesInPreviewMode") &&
        isMobile &&
        googleSlidesLinkRegex.test(fileUrl))
    ) {
      const splittedLink = fileUrl.split("/");
      splittedLink[splittedLink.length - 1] = "preview";
      fileUrl = splittedLink.join("/");
    }

    const iframeSrc = openInViewer
      ? useGoogleDocsEmbedViewer
        ? `${GOOGLE_FILE_VIEWER_URL}${encodeURIComponent(
            signedUrl || fileUrl
          )}&embedded=true`
        : `${MS_FILE_VIEWER_URL}${encodeURIComponent(signedUrl || fileUrl)}`
      : fileUrl;

    const [isLoading, setIsLoading] = useState(true);
    const [
      isGoogleDocsPlaybackViewerMounted,
      setIsGoogleDocsPlaybackViewerMounted,
    ] = useState(false);
    const [isViewerVisible, setIsViewerVisible] = useState(false);

    const iframeRef = useRef(null);

    const iframeLoaded = e => {
      if (isLoading) {
        setIsLoading(false);
      }
    };

    const onIframeMount = async () => {
      if (useIframePartialLoader) {
        await delay(1000);
        if (isLoading) {
          setIsLoading(false);
        }
      }
    };
    const onPlayBackButtonClick = () => {
      setIsGoogleDocsPlaybackViewerMounted(true);
      setIsViewerVisible(prev => !prev);
    };

    const closePlaybackViewer = () => {
      setIsViewerVisible(false);
    };
    const unmountPlaybackViewer = () => {
      setIsGoogleDocsPlaybackViewerMounted(false);
      setIsViewerVisible(false);
    };

    if (isVideoLink) {
      if (isLoading) setIsLoading(false);
    }
    return (
      <div className={classes.container}>
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <Loading />
          </div>
        ) : null}
        {showFileHeader && (
          <div className={classes.previewHeader}>{`Preview - ${name}`}</div>
        )}
        {isVideoLink ? (
          <EmbedVideoPlayer
            className={classes.embedPlayerContainer}
            url={fileUrl}
            thumbnail={file.thumbUrl}
          />
        ) : (
          <IframeWrapper
            iframeSrc={iframeSrc}
            iframeRef={iframeRef}
            iframeLoaded={iframeLoaded}
            onIframeMount={onIframeMount}
            id={id}
            title={
              name
                ? t("a11y:file_preview_with_name", { name })
                : t("a11y:file_preview")
            }
          />
        )}

        {shouldShowGiveFeedbackButton ? (
          <div className={classes.feedbackButton} onClick={onClickGiveFeedback}>
            <GiveFeedbackIcon width={16} height={16} />
            <span className={classes.feedbackButtonText}>
              {t("common:give_feedback_text")}
            </span>
          </div>
        ) : null}

        {!isLoading && showGoogleDocsPlaybackButton ? (
          <Tooltip
            dsVersion="2.0"
            placement={"topRight"}
            tooltip={t("classRoom:click_to_view_playback_history")}
            showArrow={true}
            fixArrowPosition={true}
            visible={!isViewerVisible}
          >
            <IconButton
              dsVersion="2.0"
              type="fill"
              variant="destructive"
              size="medium"
              style={{
                position: "absolute",
                bottom: 16,
                right: 16,
                backgroundColor: "var(--decorative-foreground-neutral)",
                ...playbackButtonPositionStyle,
              }}
              icon={<ArrowTriangleOutlined />}
              onClick={onPlayBackButtonClick}
              id="google-docs-playback-button"
            />
          </Tooltip>
        ) : null}
        {!isLoading &&
          showGoogleDocsPlaybackButton &&
          isGoogleDocsPlaybackViewerMounted && (
            <GoogleDocsPlaybackWrapper
              attachmentId={id}
              name={name}
              closePlaybackViewer={closePlaybackViewer}
              unmountPlaybackViewer={unmountPlaybackViewer}
              isViewerVisible={isViewerVisible}
              playbackButtonPositionStyle={playbackButtonPositionStyle}
            />
          )}
      </div>
    );
  }
);

FileViewer.displayName = "FileViewer";

FileViewer.defaultProps = {
  mode: "edit",
  showFileHeader: false,
  useIframePartialLoader: false,
};

export default I18nHOC({ resource: ["common", "a11y"] })(FileViewer);
